'use client';

import React, { useEffect, useMemo, useState } from 'react';
import { cn } from '@/lib/cn';
import type { ReviewApi } from '@repo/api';
import {
  IconEdit,
  IconDelete,
  IconDocument,
  IconCross,
  IconChevronLeft,
  IconChevronRight,
} from '@repo/svgs';
import { Link } from '@/components/ui/Link';
import { SentimentCell } from '@/components/ui/SentimentCell';
import { MaskedText, Divider, StarRating } from '@/components/ui';
import { ReviewActions } from './ReviewActions';
import { buildingUrl } from '@/utils/routes';
import type { Building } from '@repo/api';
import { titleCase } from '@repo/utils';

export interface ReviewCardProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Review data from API */
  review: ReviewApi;
  /** Whether to show the address link */
  showAddress?: boolean;
  /** Whether to mask sensitive content for non-authenticated users */
  isMasked?: boolean;
  /** Whether to show edit/delete buttons */
  showEditDelete?: boolean;
  /** Callback when edit button is clicked */
  onEditReview?: (review: ReviewApi) => void;
  /** Callback when delete button is clicked */
  onDeleteReview?: (review: ReviewApi) => void;
  /** Event screen */
  eventScreen?: 'building' | 'contact' | 'other';
}

export function ReviewCard({
  review,
  showAddress = false,
  isMasked = false,
  showEditDelete = false,
  onEditReview,
  onDeleteReview,
  className,
  eventScreen = 'other',
  ...props
}: ReviewCardProps) {
  const VIDEO_EXTS = ['.mp4', '.webm', '.mov', '.avi', '.mkv', '.flv', '.wmv', '.mpeg', '.3gp'];

  const getMediaKindFromUrl = (url?: string) => {
    const u = (url || '').toLowerCase().split('?')[0]?.split('#')[0] || '';
    if (!u) return 'unknown' as const;
    if (u.endsWith('.pdf')) return 'pdf' as const;
    if (VIDEO_EXTS.some(ext => u.endsWith(ext))) return 'video' as const;
    if (['.png', '.jpg', '.jpeg', '.webp', '.gif'].some(ext => u.endsWith(ext)))
      return 'image' as const;
    return 'unknown' as const;
  };

  const allMedia = useMemo(() => {
    const items = (review.images ?? []).slice();
    items.sort((a, b) => (a.order || 0) - (b.order || 0));
    return items;
  }, [review.images]);

  // Get display images (max 3 with +N indicator)
  const displayImages = allMedia.slice(0, 3);
  const remainingImagesCount = allMedia.length - 3;

  const [viewerOpen, setViewerOpen] = useState(false);
  const [viewerIndex, setViewerIndex] = useState(0);

  const openViewerAt = (idx: number) => {
    const nextIdx = Math.max(0, Math.min(allMedia.length - 1, idx));
    setViewerIndex(nextIdx);
    setViewerOpen(true);
  };

  const viewerItem = allMedia[viewerIndex];
  const viewerKind = getMediaKindFromUrl(viewerItem?.url);

  // Keyboard navigation for viewer
  useEffect(() => {
    if (!viewerOpen) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setViewerOpen(false);
        return;
      }
      if (e.key === 'ArrowLeft') {
        setViewerIndex(i => Math.max(0, i - 1));
        return;
      }
      if (e.key === 'ArrowRight') {
        setViewerIndex(i => Math.min(allMedia.length - 1, i + 1));
      }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [viewerOpen, allMedia.length]);

  // Determine review status for badge
  const reviewStatus = review.status?.toLowerCase();
  const showStatusBadge =
    showEditDelete &&
    (reviewStatus === 'pending' ||
      reviewStatus === 'rejected' ||
      reviewStatus === 'draft' ||
      reviewStatus === 'published');

  return (
    <div className={cn('bg-primary rounded-2xl flex flex-col gap-3', className)} {...props}>
      {/* Status Badge and Edit/Delete Buttons Row - Only shown when showEditDelete is true */}
      {showEditDelete && (
        <div className="flex justify-between items-center">
          {showStatusBadge ? (
            <div
              className={`px-2 py-1 rounded-full ${
                reviewStatus === 'pending'
                  ? 'bg-caution-low'
                  : reviewStatus === 'rejected'
                    ? 'bg-alert-low'
                    : reviewStatus === 'published'
                      ? 'bg-success-low'
                      : 'bg-tertiary'
              }`}
            >
              <span
                className={`text-caption ${
                  reviewStatus === 'pending'
                    ? 'text-caution'
                    : reviewStatus === 'rejected'
                      ? 'text-alert'
                      : reviewStatus === 'published'
                        ? 'text-success'
                        : 'text-secondary'
                }`}
              >
                {reviewStatus === 'pending'
                  ? 'Pending'
                  : reviewStatus === 'rejected'
                    ? 'Rejected'
                    : reviewStatus === 'published'
                      ? 'Published'
                      : 'Draft'}
              </span>
            </div>
          ) : (
            <div />
          )}
          <div className="flex items-center gap-2">
            {onEditReview && (
              <button
                type="button"
                onClick={() => onEditReview(review)}
                className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-primary-hover active:bg-primary-press transition-colors"
                aria-label="Edit review"
              >
                <IconEdit className="w-4 h-4 icon-secondary" aria-hidden />
              </button>
            )}
            {onDeleteReview && (
              <button
                type="button"
                onClick={() => onDeleteReview(review)}
                className="w-9 h-9 rounded-full flex items-center justify-center hover:bg-primary-hover active:bg-primary-press transition-colors"
                aria-label="Delete review"
              >
                <IconDelete className="w-4 h-4 icon-secondary" aria-hidden />
              </button>
            )}
          </div>
        </div>
      )}

      {/* Top container: Rating, Meta, Title, and Sentiment badges */}
      <div className="flex flex-col gap-2">
        {/* Rating and Meta row */}
        <div className="flex items-center justify-between">
          <StarRating className="w-4 h-4" rating={review.rating || 0} />

          {/* Tenant status and timestamp */}
          <div className="flex items-center gap-1 text-caption text-secondary">
            <span>{review.isCurrentTenant ? 'Current tenant' : 'Former tenant'}</span>
            <span>·</span>
            <span>{review.createdTime}</span>
          </div>
        </div>

        {/* Review title */}
        <div className="text-body-1-high text-primary">
          <h3>{review.title}</h3>
        </div>

        {/* Address link (conditional) - appears before badges when shown */}
        {showAddress && review.fullAddress && (
          <Link
            href={buildingUrl({
              slug: review.buildingSlug,
              primaryAddress: {
                cityId: review.cityId || 'nyc',
                slug: review.addressSlug,
              },
              neighborhood: { city: review.cityId || 'nyc' },
            } as unknown as Building)}
            className="inline-block text-body-2 text-accent hover:underline"
          >
            {titleCase(review.fullAddress)}
          </Link>
        )}

        {/* Building and Owner badges - hidden when masked */}
        {!isMasked && (
          <div className="flex items-center gap-2">
            {/* Building Recommendation */}
            {review.recommendBuilding !== undefined && (
              <SentimentCell
                type={review.recommendBuilding ? 'positive' : 'negative'}
                label="Building"
              />
            )}
            {/* Dot separator if both are shown */}
            {review.recommendBuilding !== undefined && review.approveOwner !== undefined && (
              <span className="text-body-2 text-secondary">·</span>
            )}
            {/* Owner Approval */}
            {review.approveOwner !== undefined && (
              <SentimentCell type={review.approveOwner ? 'positive' : 'negative'} label="Owner" />
            )}
          </div>
        )}
      </div>

      <Divider variant="tertiary" />

      {/* Review content sections */}
      <div className="flex flex-col gap-3">
        {/* Pros */}
        {(review.pros || isMasked) && (
          <div className="flex flex-col gap-1">
            <h4 className="text-body-2-high text-primary">Pros</h4>
            {isMasked ? (
              <div className="space-y-1">
                <MaskedText className="w-full" />
                <MaskedText className="w-1/2" />
              </div>
            ) : (
              <p className="text-body-2 text-primary">{review.pros}</p>
            )}
          </div>
        )}

        {/* Cons */}
        {(review.cons || isMasked) && (
          <div className="flex flex-col gap-1">
            <h4 className="text-body-2-high text-primary">Cons</h4>
            {isMasked ? (
              <MaskedText className="w-3/4" />
            ) : (
              <p className="text-body-2 text-primary">{review.cons}</p>
            )}
          </div>
        )}

        {/* Advice */}
        {(review.advice || isMasked) && (
          <div className="flex flex-col gap-1">
            <h4 className="text-body-2-high text-primary">Advice to the owners</h4>
            {isMasked ? (
              <MaskedText className="w-1/4" />
            ) : (
              <p className="text-body-2 text-primary">{review.advice}</p>
            )}
          </div>
        )}
      </div>

      {/* Images */}
      {displayImages.length > 0 && (
        <div className="flex gap-2">
          {displayImages.map((image, index) => {
            const kind = getMediaKindFromUrl(image.url);
            return (
              <button
                type="button"
                key={image.id}
                className="relative w-20 h-20 rounded-xl overflow-hidden bg-secondary"
                onClick={() => openViewerAt(index)}
                aria-label="Open media"
              >
                {kind === 'video' ? (
                  <video
                    src={image.url}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                    preload="metadata"
                  />
                ) : kind === 'pdf' || kind === 'unknown' ? (
                  <div className="w-full h-full flex items-center justify-center bg-secondary">
                    <IconDocument className="w-6 h-6 icon-secondary" aria-hidden />
                  </div>
                ) : (
                  <img
                    src={image.url}
                    alt={`Review image ${index + 1}`}
                    className="w-full h-full object-cover"
                  />
                )}

                {(kind === 'video' || kind === 'pdf') && (
                  <div className="absolute left-2 top-2 rounded-full bg-primary/90 px-2 py-0.5">
                    <span className="text-[10px] leading-none text-secondary">
                      {kind === 'video' ? 'VIDEO' : 'PDF'}
                    </span>
                  </div>
                )}
              </button>
            );
          })}
          {remainingImagesCount > 0 && (
            <button
              type="button"
              className="flex items-center justify-center w-20 h-20 rounded-xl bg-secondary border border-secondary"
              onClick={() => openViewerAt(3)}
              aria-label="Open more media"
            >
              <span className="text-body-2-high text-accent">+{remainingImagesCount}</span>
            </button>
          )}
        </div>
      )}

      {/* Action buttons - hidden when masked */}
      {!isMasked && !review.isSelfReviewed && (
        <>
          <Divider variant="tertiary" />
          <ReviewActions
            reviewId={review.id || ''}
            {...(review.addressSlug ? { addressSlug: review.addressSlug } : {})}
            helpfulCount={review?.helpfulCount || 0}
            isHelpful={review?.isHelpful || false}
            eventScreen={eventScreen}
          />
        </>
      )}

      {/* Fullscreen media viewer */}
      {viewerOpen && viewerItem?.url ? (
        <div
          className="fixed inset-0 z-60 bg-black flex flex-col"
          onClick={() => setViewerOpen(false)}
        >
          {/* Header */}
          <div className="absolute text-center top-[calc(env(safe-area-inset-top)+1.5rem)] left-4 right-4 z-20 flex items-center justify-center pointer-events-none">
            <span className="text-base font-semibold text-white truncate max-w-[80vw]">
              {viewerKind === 'video' ? 'Video' : viewerKind === 'pdf' ? 'PDF' : 'Media'}{' '}
              {allMedia.length > 1 ? `${viewerIndex + 1}/${allMedia.length}` : ''}
            </span>
          </div>

          {/* Close */}
          <button
            type="button"
            onClick={e => {
              e.stopPropagation();
              setViewerOpen(false);
            }}
            className="absolute top-[calc(env(safe-area-inset-top)+1.5rem)] right-4 lg:right-12 w-10 h-10 rounded-full z-30 flex items-center justify-center transition-colors pointer-events-auto"
            aria-label="Close"
          >
            <IconCross width={24} height={24} style={{ color: 'var(--icon-color-inverted)' }} />
          </button>

          {/* Prev/Next */}
          {allMedia.length > 1 ? (
            <>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  setViewerIndex(i => Math.max(0, i - 1));
                }}
                disabled={viewerIndex <= 0}
                className={cn(
                  'absolute left-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full flex items-center justify-center',
                  'bg-black/30 hover:bg-black/40 transition-colors',
                  viewerIndex <= 0 && 'opacity-40 pointer-events-none',
                )}
                aria-label="Previous"
              >
                <IconChevronLeft
                  className="w-6 h-6"
                  style={{ color: 'var(--icon-color-inverted)' }}
                />
              </button>
              <button
                type="button"
                onClick={e => {
                  e.stopPropagation();
                  setViewerIndex(i => Math.min(allMedia.length - 1, i + 1));
                }}
                disabled={viewerIndex >= allMedia.length - 1}
                className={cn(
                  'absolute right-3 top-1/2 -translate-y-1/2 z-30 w-10 h-10 rounded-full flex items-center justify-center',
                  'bg-black/30 hover:bg-black/40 transition-colors',
                  viewerIndex >= allMedia.length - 1 && 'opacity-40 pointer-events-none',
                )}
                aria-label="Next"
              >
                <IconChevronRight
                  className="w-6 h-6"
                  style={{ color: 'var(--icon-color-inverted)' }}
                />
              </button>
            </>
          ) : null}

          {/* Content */}
          <div
            className="flex-1 flex items-center justify-center lg:p-20 p-6 pt-18 lg:pt-20 min-h-0"
            onClick={e => e.stopPropagation()}
          >
            <div className="relative w-full h-full max-w-full max-h-full">
              {viewerKind === 'image' ? (
                <img src={viewerItem.url} alt="Media" className="w-full h-full object-contain" />
              ) : viewerKind === 'video' ? (
                <video
                  src={viewerItem.url}
                  className="w-full h-full object-contain"
                  controls
                  playsInline
                />
              ) : (
                <iframe
                  title="Document viewer"
                  src={viewerItem.url}
                  className="w-full h-full rounded-xl bg-white"
                />
              )}
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
